import type { TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { FilterTypes } from '@/constants/FilterTypes';
import { _isNotBlank } from '@/littledash';
import type { AsyncExportState } from '@/model/AsyncExport.model';
import { mapTimepointIntervalOperation } from '@/utils/TableFilter.utils';

const METADATA_PREFIX = 'metadata.';

const operatorFor = (name: FilterTypes): string | null =>
  Object.keys(FilterTypes).find((key) => FilterTypes[key as keyof typeof FilterTypes] === name) ?? null;

/**
 * A between bound is present when it is a number or a non-blank string. Blank or missing
 * bounds are dropped so we never emit a malformed `field|between|,` string (BNCH-162468).
 */
const hasBound = (bound: string | number | undefined): boolean => typeof bound === 'number' || _isNotBlank(bound);

const encodeFilter = (filter: TableFilter): string | null => {
  const isMetadata = String(filter.category.value).includes(METADATA_PREFIX);
  if (isMetadata) {
    return null;
  }

  const rawOperator = operatorFor(filter.operation.name);
  // Timepoint filters use interval-suffixed comparators the backend rejects; normalize
  // them to the base comparator, matching the list fetch path (BNCH-162468).
  const operator =
    rawOperator !== null ? mapTimepointIntervalOperation(filter.category.value, rawOperator) : rawOperator;
  const { value, from, to } = filter.option;

  if (operator === 'between' || operator === 'between_interval') {
    if (hasBound(from) && hasBound(to)) {
      return `${filter.category.value}|${operator}|${from},${to}`;
    }
    return null;
  }

  if (operator !== null && _isNotBlank(value)) {
    return `${filter.category.value}|${operator}|${value}`;
  }

  if (filter.operation.name === FilterTypes.IS_NULL || filter.operation.name === FilterTypes.EMPTY) {
    return `${filter.category.value}|eq|null`;
  }

  if (filter.operation.name === FilterTypes.IS_NOT_NULL) {
    return `${filter.category.value}|ne|null`;
  }

  return null;
};

/**
 * Flattens APITable view filters into the `field|op|value` strings that the
 * legacy PHP filter pipeline expects. Returns null when the export route opts
 * out of filters (the "All X" variants) or when nothing is encodable. The
 * branching rules mirror `generateFilterParams` in `APITable.utils.ts`.
 */
export const buildAsyncExportFilterStrings = (state: AsyncExportState): Array<string> | null => {
  if (state.route.noFilters === true) {
    return null;
  }
  const strings = (state.view.filters ?? []).reduce<Array<string>>((acc, filter) => {
    const encoded = encodeFilter(filter);
    if (encoded !== null) {
      acc.push(encoded);
    }
    return acc;
  }, []);
  return strings.length > 0 ? strings : null;
};
